import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ListingCard from '../components/ListingCard';
import BannerAd from '../components/BannerAd';
import SriLankaHero from '../components/SriLankaHero';
import { ChevronDown, ChevronRight, ChevronUp, Search, Shield, Zap, Users, Loader2, Star, Clock, TrendingUp, Flame } from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category Sidebar Component
const CategorySidebar = ({ categories, onCategoryClick }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (slug) => {
    setExpandedCategories(prev => ({
      ...prev,
      [slug]: !prev[slug]
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3">
        <h2 className="text-white font-bold text-lg">Popular Categories</h2>
      </div>
      <div className="divide-y divide-gray-100 max-h-[calc(100vh-200px)] overflow-y-auto">
        {categories.map((category) => (
          <div key={category.id} className="group">
            {/* Main Category Row */}
            <div 
              className="flex items-center justify-between px-4 py-3 cursor-pointer transition-colors hover:bg-blue-50"
            >
              <div className="flex items-center flex-1" onClick={() => onCategoryClick(category.slug)}>
                <span className="text-xl mr-3 notranslate" translate="no">{category.icon}</span>
                <span className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors text-sm">
                  {category.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full min-w-[28px] text-center notranslate" translate="no">
                  {category.ad_count}
                </span>
                {category.subcategories?.length > 0 && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category.slug);
                    }}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                  >
                    {expandedCategories[category.slug] ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Subcategories */}
            {category.subcategories?.length > 0 && expandedCategories[category.slug] && (
              <div className="bg-gray-50 border-t border-gray-100">
                {category.subcategories.map((sub) => (
                  <div
                    key={sub.id || sub.slug}
                    onClick={() => onCategoryClick(sub.slug)}
                    className="flex items-center justify-between px-4 py-2 pl-12 cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    <span className="text-sm text-gray-600 hover:text-blue-600">
                      {sub.name}
                    </span>
                    <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded-full border notranslate" translate="no">
                      {sub.ad_count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Ad Row Component for Category Sections
const AdRow = ({ title, icon, ads, viewAllLink, bgColor = "bg-white", badge = null }) => {
  if (!ads || ads.length === 0) return null;
  
  return (
    <div className={`${bgColor} rounded-lg shadow-sm border p-4 mb-4`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="notranslate" translate="no">{icon}</span>
          <h3 className="font-bold text-lg text-gray-800">{title}</h3>
          {badge && (
            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {viewAllLink && (
          <Link to={viewAllLink} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
            View All <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ads.slice(0, 4).map((listing) => (
          <ListingCard key={listing.id} listing={listing} compact />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [countryContent, setCountryContent] = useState({
    hero_title: "Buy & Sell Anything",
    hero_subtitle: "What if someone is already looking for what you have?",
    hero_punchline: "A product. A service. A property. A job opportunity. A business.",
    hero_cta_line: "Explore or post your first ad today.",
    tagline: "Your trusted marketplace"
  });
  const navigate = useNavigate();
  const { country, countryConfig, loading: countryLoading } = useCountry();

  // Fetch country-specific content
  const fetchCountryContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/localization/public/content/${country}`);
      if (response.data.success && response.data.content) {
        // Merge so newer fields stay populated even if the DB doc is old
        setCountryContent(prev => ({ ...prev, ...response.data.content }));
      }
    } catch (err) {
      // Use default content based on country name
      setCountryContent({
        hero_title: `Buy & Sell Anything in ${countryConfig?.name || country}`,
        hero_subtitle: "What if someone is already looking for what you have?",
        hero_punchline: "A product. A service. A property. A job opportunity. A business.",
        hero_cta_line: "Explore or post your first ad today.",
        tagline: `${countryConfig?.name || country}'s most trusted marketplace`
      });
    }
  }, [country, countryConfig]);

  // Fetch content when country changes
  useEffect(() => {
    fetchCountryContent();
  }, [fetchCountryContent]);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      const params = country ? `?country_code=${country}` : '';
      const response = await axios.get(`${API_URL}/api/listings/homepage-data${params}`);
      setHomeData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching homepage data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    // Wait until CountryContext finishes detecting country (avoids fetching with stale default)
    if (countryLoading) return;
    fetchHomeData();
    
    // Refresh every 15 minutes for rotating ads
    const interval = setInterval(fetchHomeData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHomeData, countryLoading]);

  const handleCategoryClick = (slug) => {
    navigate(`/category/${slug}`);
  };

  // Get categories that have ads (more than 1 item) for display
  const categoriesWithAds = homeData?.categories?.filter(
    cat => homeData?.category_ads?.[cat.slug]?.length >= 2
  ) || [];

  // Split categories: first 8 visible, rest hidden behind "Show More"
  const visibleCategoryAds = showAllCategories 
    ? categoriesWithAds 
    : categoriesWithAds.slice(0, 8);
  
  const hiddenCategoriesCount = categoriesWithAds.length - 8;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Sri Lanka gets bespoke premium hero, others keep the shared hero */}
      {country === 'SL' ? (
        <SriLankaHero content={countryContent} />
      ) : (
      <section className="relative px-4 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 overflow-hidden" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        {/* Soft accent blobs */}
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-pink-300/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Big H1 - was "Buy & Sell Anything in <Country>", now the question */}
          <h1
            className="font-bold text-white mb-3 tracking-tight"
            style={{ fontSize: '2.25rem' }}
            data-testid="hero-title"
          >
            {countryContent.hero_title}
          </h1>

          {/* Sub-headline */}
          <p
            className="font-semibold text-white/95 leading-snug max-w-3xl mx-auto"
            style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}
            data-testid="hero-subtitle"
          >
            {countryContent.hero_subtitle}
          </p>

          {/* Punchline list - categories teaser */}
          {countryContent.hero_punchline && (
            <p
              className="text-base md:text-lg text-blue-50/95 max-w-3xl mx-auto mb-2"
              data-testid="hero-punchline"
            >
              {countryContent.hero_punchline}
            </p>
          )}

          {/* CTA line - accent colored */}
          {countryContent.hero_cta_line && (
            <p
              className="text-base md:text-lg font-semibold text-yellow-200 mb-7"
              data-testid="hero-cta-line"
            >
              {countryContent.hero_cta_line}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/post-ad">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 hover:scale-[1.02] active:scale-100 transition-transform rounded-full px-8 font-semibold shadow-lg"
                data-testid="hero-post-ad-btn"
              >
                Post An Ad Free
              </Button>
            </Link>
            <Link to="/category/all">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white/70 hover:bg-white/10 rounded-full px-8 font-semibold"
                data-testid="hero-browse-all-btn"
              >
                Browse All Ads
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-20">
              {loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <CategorySidebar 
                  categories={homeData?.categories || []} 
                  onCategoryClick={handleCategoryClick}
                />
              )}
            </div>
          </aside>

          {/* Right Content - Ads Sections */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
            ) : error ? (
              <div className="text-center py-20 bg-white rounded-lg shadow-sm border">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={fetchHomeData} variant="outline">
                  Try Again
                </Button>
              </div>
            ) : (
              <>
                {/* Row 1: Featured Ads */}
                <AdRow
                  title="Featured Ads"
                  icon={<Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />}
                  ads={homeData?.featured_ads}
                  viewAllLink="/category/all?filter=featured"
                  bgColor="bg-gradient-to-r from-yellow-50 to-orange-50"
                />

                {/* Row 2: Recent Ads (only 4 items - 1 row) */}
                <AdRow
                  title="Recent Ads"
                  icon={<Clock className="h-5 w-5 text-blue-600" />}
                  ads={homeData?.recent_ads}
                  viewAllLink="/category/all?sort=recent"
                  bgColor="bg-white"
                  badge="Just Listed"
                />

                {/* Banner Ad - appears right after Recent Ads */}
                <div className="my-5" data-testid="home-banner-after-recent">
                  <BannerAd position="home_top" />
                </div>

                {/* Row 3: Popular Ads (based on views) */}
                <AdRow
                  title="Popular Ads"
                  icon={<Flame className="h-5 w-5 text-orange-500" />}
                  ads={homeData?.popular_ads}
                  viewAllLink="/category/all?sort=popular"
                  bgColor="bg-gradient-to-r from-orange-50 to-red-50"
                  badge="Trending"
                />

                {/* Row 4+: Category-Based Ads (with Show More after 8) */}
                {visibleCategoryAds.map((category) => (
                  <AdRow
                    key={category.slug}
                    title={category.name}
                    icon={<span className="text-xl">{category.icon}</span>}
                    ads={homeData?.category_ads?.[category.slug]}
                    viewAllLink={`/category/${category.slug}`}
                    bgColor="bg-white"
                  />
                ))}

                {/* Show More / Show Less Button */}
                {hiddenCategoriesCount > 0 && (
                  <div className="flex justify-center my-6">
                    <Button
                      variant="outline"
                      onClick={() => setShowAllCategories(!showAllCategories)}
                      className="rounded-full px-8 py-2 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                    >
                      {showAllCategories ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          Show Less Categories
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Show {hiddenCategoriesCount} More Categories
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Show message if no ads */}
                {!homeData?.featured_ads?.length && 
                 !homeData?.recent_ads?.length && 
                 !homeData?.popular_ads?.length &&
                 categoriesWithAds.length === 0 && (
                  <div className="text-center py-20 bg-white rounded-lg shadow-sm border">
                    <p className="text-gray-500 text-lg mb-4">No listings available yet</p>
                    <Link to="/post-ad">
                      <Button className="bg-gradient-to-r from-blue-600 to-purple-600">
                        Post the First Ad
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Why Choose Us - Compact */}
      <section className="py-10 px-4 bg-white border-t">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold mb-1">Why Choose Zeilian?</h2>
            <p className="text-gray-600 text-sm" data-testid="tagline">{countryContent.tagline}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 text-center hover:shadow-lg transition-shadow border-0 bg-blue-50">
              <div className="inline-block p-2 bg-blue-100 rounded-full mb-2">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Connect With Confidence</h3>
              <p className="text-gray-600 text-xs">Verification, transparency and trusted marketplace safeguards help you connect with genuine people and opportunities.</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow border-0 bg-purple-50">
              <div className="inline-block p-2 bg-purple-100 rounded-full mb-2">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Reach The Right People</h3>
              <p className="text-gray-600 text-xs">Whether you’re selling, hiring, renting or promoting services, Zeilian helps you connect with people actively searching.</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow border-0 bg-green-50">
              <div className="inline-block p-2 bg-green-100 rounded-full mb-2">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Everything In One Place</h3>
              <p className="text-gray-600 text-xs">Products, services, vehicles, property, jobs, education, insurance and business opportunities all in one platform.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-10 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
            Ready to Start Selling?
          </h2>
          <p className="text-base text-blue-100 mb-1">
            Every listing is assessed using Zeilian Trust Intelligence<sup className="text-[0.55em] align-super">™</sup> to help you identify trustworthy providers and opportunities before you engage.
          </p>
          <p className="text-base md:text-lg font-semibold text-yellow-200 mb-5">
            Where Opportunities Find People.
          </p>
          <Link to="/post-ad">
            <Button size="default" className="bg-white text-blue-600 hover:bg-gray-100 rounded-full px-6">
              Post An Ad Free
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
